export type UnitSystem = 'metric' | 'imperial';

/**
 * Converts degrees to radians.
 */
function toRadians(degrees: number): number {
  return (degrees * Math.PI) / 180;
}

/**
 * Converts radians to degrees.
 */
function toDegrees(radians: number): number {
  return (radians * 180) / Math.PI;
}

/**
 * Calculates great-circle distance between two GPS coordinates using the Haversine formula.
 * Returns distance in meters.
 */
export function calculateDistanceMeters(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const earthRadiusMeters = 6371000;
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) *
      Math.cos(toRadians(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return earthRadiusMeters * c;
}

/**
 * Calculates the great-circle initial bearing in degrees (0 - 359.9) from point 1 to point 2.
 */
export function calculateBearing(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const phi1 = toRadians(lat1);
  const phi2 = toRadians(lat2);
  const deltaLambda = toRadians(lon2 - lon1);

  const y = Math.sin(deltaLambda) * Math.cos(phi2);
  const x =
    Math.cos(phi1) * Math.sin(phi2) -
    Math.sin(phi1) * Math.cos(phi2) * Math.cos(deltaLambda);

  const theta = Math.atan2(y, x);
  return (toDegrees(theta) + 360) % 360;
}

/**
 * Calculates relative angle on compass needle normalized to [0, 360):
 * relativeAngle = (targetBearing - deviceHeading) mod 360
 */
export function calculateRelativeAngle(
  targetBearing: number,
  deviceHeading: number
): number {
  return ((targetBearing - deviceHeading) % 360 + 360) % 360;
}

/**
 * Calculates the shortest signed angular difference (newAngle - currentAngle) in degrees (-180 to +180).
 */
export function calculateShortestAngleDiff(newAngle: number, currentAngle: number): number {
  let diff = (newAngle - currentAngle) % 360;
  if (diff > 180) diff -= 360;
  if (diff < -180) diff += 360;
  return diff;
}

/**
 * Applies a low-pass filter (Exponential Moving Average) with deadband to smooth compass heading.
 * Handles the 0/360 boundary correctly without spinning glitches.
 *
 * @param currentAngle Current continuous heading angle in degrees
 * @param targetAngle Raw target heading in degrees (0 - 359.9)
 * @param alpha Smoothing factor between 0.0 (no update) and 1.0 (immediate raw update). Default: 0.18
 * @param deadband Minimum angular delta in degrees to respond to. Default: 0.5
 */
export function smoothHeading(
  currentAngle: number,
  targetAngle: number,
  alpha = 0.18,
  deadband = 0.5
): number {
  const diff = calculateShortestAngleDiff(targetAngle, currentAngle);
  if (Math.abs(diff) < deadband) {
    return currentAngle;
  }
  return currentAngle + diff * alpha;
}

/**
 * Formats a distance in meters into human-readable string according to unit preferences.
 */
export function formatDistance(
  meters: number,
  unitSystem: UnitSystem
): string {
  if (meters == null || isNaN(meters)) return '—';

  if (unitSystem === 'metric') {
    if (meters < 1000) {
      return `${Math.round(meters)} m`;
    }
    const km = meters / 1000;
    return km >= 100 ? `${Math.round(km)} km` : `${km.toFixed(1)} km`;
  }

  // Imperial
  const feet = meters * 3.28084;
  if (feet < 1000) {
    return `${Math.round(feet)} ft`;
  }
  const miles = meters * 0.000621371;
  return miles >= 100 ? `${Math.round(miles)} mi` : `${miles.toFixed(1)} mi`;
}

export interface DeviceOrientationInput {
  alpha: number | null;
  beta: number | null;
  gamma: number | null;
  webkitCompassHeading?: number;
}

export interface DeviceHeadingResult {
  heading: number;
  pitchTiltDegrees: number;
  calibrationOffset: number;
}

/**
 * Calculates continuous compass heading, smoothly transitioning the reference pointing axis
 * from the top edge of the device (+Y) when held flat, to the camera/horizon line of sight (-Z)
 * when held upright in portrait mode.
 *
 * This eliminates the gimbal lock singularity where the horizontal projection of +Y collapses to 0
 * as pitch approaches 90 degrees, preventing erratic needle jitter when holding the phone upright.
 *
 * @param input Raw DeviceOrientationEvent data (alpha, beta, gamma, webkitCompassHeading)
 * @param previousCalibrationOffset Retained offset between WebKit compass heading and Euler yaw
 */
export function calculateDeviceHeading(
  input: DeviceOrientationInput,
  previousCalibrationOffset = 0
): DeviceHeadingResult | null {
  const { alpha, beta, gamma, webkitCompassHeading } = input;

  // Fallback 1: Missing pitch or roll data (e.g. desktop web or simulated device)
  if (beta == null || gamma == null || isNaN(beta) || isNaN(gamma)) {
    let fallbackHeading: number | null = null;
    if (webkitCompassHeading !== undefined && !isNaN(webkitCompassHeading)) {
      fallbackHeading = ((webkitCompassHeading % 360) + 360) % 360;
    } else if (alpha != null && !isNaN(alpha)) {
      fallbackHeading = (((360 - alpha) % 360) + 360) % 360;
    }
    if (fallbackHeading == null) return null;
    return {
      heading: fallbackHeading,
      pitchTiltDegrees: 0,
      calibrationOffset: previousCalibrationOffset,
    };
  }

  // If alpha is also missing, but webkitCompassHeading exists:
  if ((alpha == null || isNaN(alpha)) && webkitCompassHeading !== undefined && !isNaN(webkitCompassHeading)) {
    return {
      heading: ((webkitCompassHeading % 360) + 360) % 360,
      pitchTiltDegrees: Math.max(0, Math.min(90, beta)),
      calibrationOffset: previousCalibrationOffset,
    };
  }

  if (alpha == null || isNaN(alpha)) {
    return null;
  }

  // Convert angles to radians
  const a = toRadians(alpha);
  const b = toRadians(beta);
  const g = toRadians(gamma);

  const ca = Math.cos(a), sa = Math.sin(a);
  const cb = Math.cos(b), sb = Math.sin(b);
  const cg = Math.cos(g), sg = Math.sin(g);

  // Rotation matrix from Device coordinate frame to Earth coordinate frame:
  // R = Rz(alpha) * Rx(beta) * Ry(gamma)
  //
  // Earth coordinate system:
  // X = East, Y = North, Z = Up
  //
  // Device coordinate system:
  // X = Right, Y = Top edge of screen, Z = Perpendicular out of screen towards user
  //
  // Relevant columns of R (columns 2 and 3 corresponding to device Y and Z):
  const r12 = -sa * cb;
  const r13 = ca * sg + sa * sb * cg;
  const r22 = ca * cb;
  const r23 = sa * sg - ca * sb * cg;
  const r32 = sb;
  const r33 = cb * cg;

  // Earth Up vector in device coordinates:
  // u = [ -cb*sg, sb, cb*cg ]
  // Pitch tilt angle from horizontal:
  // Flat on table face up: sb = 0, cb*cg = 1 -> tilt = 0
  // Upright portrait facing user: sb = 1, cb*cg = 0 -> tilt = pi/2 (90 deg)
  const pitchTiltRad = Math.atan2(r32, r33);
  const pitchTiltDegrees = (pitchTiltRad * 180) / Math.PI;

  // Clamp tilt transition angle to [0, pi/2] for forward tilt blending:
  // When flat (tilt <= 0), use +Y.
  // When upright (tilt >= pi/2), use -Z (out the back of phone).
  const tilt = Math.max(0, Math.min(Math.PI / 2, pitchTiltRad));
  const cosTilt = Math.cos(tilt);
  const sinTilt = Math.sin(tilt);

  // Blended pointing reference vector in device coordinates:
  // v_dev = cos(tilt) * (+Y) + sin(tilt) * (-Z) = [0, cos(tilt), -sin(tilt)]^T
  // Transform to Earth coordinate frame: v_earth = R * v_dev
  const vx = r12 * cosTilt - r13 * sinTilt;
  const vy = r22 * cosTilt - r23 * sinTilt;

  // Continuous azimuth heading of the blended pointing vector
  const rawAzimuth = (Math.atan2(vx, vy) * 180) / Math.PI;
  let computedHeading = ((rawAzimuth % 360) + 360) % 360;

  // On iOS, webkitCompassHeading gives magnetic north, but alpha is relative.
  // We calibrate the delta between webkitCompassHeading and the calculated heading of +Y
  // whenever the phone is in a level, low-tilt orientation (< 45 deg), where webkitCompassHeading
  // is known to be highly accurate and stable.
  let newCalibrationOffset = previousCalibrationOffset;
  if (webkitCompassHeading !== undefined && !isNaN(webkitCompassHeading)) {
    const normWebkit = ((webkitCompassHeading % 360) + 360) % 360;

    // Calculate heading of device +Y only:
    const yHeading = (((Math.atan2(r12, r22) * 180) / Math.PI % 360) + 360) % 360;
    const instantaneousDelta = (((normWebkit - yHeading) % 360) + 360) % 360;

    if (Math.abs(beta) < 45 && Math.abs(gamma) < 45) {
      if (previousCalibrationOffset === 0) {
        newCalibrationOffset = instantaneousDelta;
      } else {
        // Smooth calibration offset to eliminate micro-fluctuations
        newCalibrationOffset = smoothHeading(previousCalibrationOffset, instantaneousDelta, 0.1, 0.2);
      }
    }

    computedHeading = ((computedHeading + newCalibrationOffset) % 360 + 360) % 360;
  }

  return {
    heading: computedHeading,
    pitchTiltDegrees,
    calibrationOffset: newCalibrationOffset,
  };
}

/**
 * Determines whether a user is currently considered online based on the 15-second heartbeat threshold.
 */
export function isUserOnline(
  updatedAtUnix?: number,
  currentUnix: number = Math.floor(Date.now() / 1000),
  timeoutSeconds: number = 15
): boolean {
  if (!updatedAtUnix || updatedAtUnix <= 0) return false;
  return currentUnix - updatedAtUnix <= timeoutSeconds;
}

export interface MutualAlignmentResult {
  isLooking: boolean;
  step: number; // 0 (none), 1 (15-20 deg), 2 (10-15 deg), 3 (5-10 deg), 4 (0-5 deg)
  maxErrorDegrees: number;
}

/**
 * Calculates whether two users are looking towards each other and returns the stepped intensity (0 to 4).
 * Both users must be facing towards each other within the maximum 20-degree cone.
 */
export function calculateMutualAlignment(
  userHeading: number,
  userLat: number,
  userLon: number,
  partnerHeading: number | undefined | null,
  partnerLat: number,
  partnerLon: number,
  partnerIsOnline: boolean
): MutualAlignmentResult {
  if (!partnerIsOnline || partnerHeading == null || isNaN(partnerHeading)) {
    return { isLooking: false, step: 0, maxErrorDegrees: Infinity };
  }

  // Bearing from user to partner
  const bearingToPartner = calculateBearing(userLat, userLon, partnerLat, partnerLon);
  // Bearing from partner to user
  const bearingToUser = calculateBearing(partnerLat, partnerLon, userLat, userLon);

  // Angular error of user pointing at partner
  const userError = Math.abs(calculateShortestAngleDiff(userHeading, bearingToPartner));
  // Angular error of partner pointing at user
  const partnerError = Math.abs(calculateShortestAngleDiff(partnerHeading, bearingToUser));

  const maxError = Math.max(userError, partnerError);

  if (maxError > 20) {
    return { isLooking: false, step: 0, maxErrorDegrees: maxError };
  }

  let step = 1;
  if (maxError <= 5) {
    step = 4;
  } else if (maxError <= 10) {
    step = 3;
  } else if (maxError <= 15) {
    step = 2;
  } else {
    step = 1;
  }

  return { isLooking: true, step, maxErrorDegrees: maxError };
}

/**
 * Returns the stepped pink/red highlight color for mutual alignment, or fallbackColor if not aligned.
 */
export function getMutualAlignmentBubbleColor(step: number, fallbackColor: string): string {
  switch (step) {
    case 1:
      return '#fbcfe8'; // Level 1 (15°-20°): Light pink tint
    case 2:
      return '#f472b6'; // Level 2 (10°-15°): Soft pink
    case 3:
      return '#fb7185'; // Level 3 (5°-10°): Warm pink / coral
    case 4:
      return '#e63946'; // Level 4 (<5°): Vibrant red / pink
    default:
      return fallbackColor;
  }
}

